<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed } from "vue";

import { useIsTouch } from "@/composables/shared/useIsTouch";

const { t } = useI18n();

const isTouch = useIsTouch();

const props = withDefaults(
  defineProps<{
    imgSrc?: string;
    icon?: string;
    isHovered: boolean;
    label?: string;
    optional?: boolean;
    themeAwareIcon?: boolean;
    variant?: "default" | "avatar";
  }>(),
  { variant: "default" }
);

const hasMeta = computed(() => !!props.label || props.optional);

const resolvedIcon = computed(
  () => props.icon ?? (props.variant === "avatar" ? "mdi-camera-plus-outline" : undefined)
);
</script>

<template>
  <div
    v-if="variant === 'avatar'"
    class="tw:pointer-events-none tw:flex tw:h-full tw:w-full tw:min-h-0 tw:flex-col tw:items-center tw:justify-center tw:overflow-hidden tw:text-center"
  >
    <div
      class="avatar-placeholder-shadow tw:mb-3 tw:sm:mb-4 tw:flex tw:h-[56px] tw:w-[56px] tw:sm:h-[72px] tw:sm:w-[72px] tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-3xl tw:bg-surface tw:transition-transform tw:duration-200"
      :class="isHovered ? 'tw:scale-105' : ''"
    >
      <img
        v-if="imgSrc"
        :src="imgSrc"
        alt=""
        class="tw:max-h-full tw:max-w-full tw:object-contain tw:transition-opacity tw:duration-200"
        :class="[
          isHovered ? 'tw:opacity-100 tw:scale-102' : 'tw:opacity-70',
          themeAwareIcon ? 'tw:dark:brightness-0 tw:dark:invert' : '',
        ]"
      />
      <v-icon
        v-else
        :icon="resolvedIcon"
        size="28"
        class="tw:text-secondaryDarker"
      />
    </div>

    <div
      v-if="label"
      class="tw:max-w-[18rem] tw:text-base tw:font-bold tw:leading-snug tw:text-on-surface tw:sm:text-lg"
    >
      {{ label }}
    </div>

    <div
      v-if="optional"
      class="tw:mt-0.5 tw:text-xs tw:font-bold tw:text-on-surface/60"
    >
      {{ t("generic.status.optionalBracket") }}
    </div>

    <v-btn
      rounded="lg"
      tabindex="-1"
      variant="text"
      color="secondaryDarker"
      prepend-icon="mdi-upload-outline"
      class="tw:mt-4 tw:pointer-events-auto tw:border tw:border-secondaryDarker/20 tw:bg-secondaryDarker/5 tw:font-bold"
    >
      {{ t("generic.action.uploadEntity", { entity: label || t("generic.fields.photo") }) }}
    </v-btn>
  </div>

  <div
    v-else
    class="tw:pointer-events-none tw:flex tw:h-full tw:w-full tw:min-h-0 tw:flex-col tw:items-center tw:justify-center tw:overflow-hidden tw:text-center"
  >
    <div
      v-if="hasMeta"
      class="tw:mb-1 tw:sm:mb-2 tw:flex tw:max-w-[18rem] tw:shrink-0 tw:flex-col tw:items-center tw:gap-1"
    >
      <div
        v-if="label"
        class="upload-field-label tw:max-w-full tw:text-xs tw:sm:text-sm tw:md:text-md tw:font-semibold tw:leading-snug tw:text-on-surface"
      >
        {{ label }}
      </div>

      <div
        v-if="optional"
        class="tw:inline-flex tw:items-center tw:rounded-full tw:px-2.5 tw:py-0.5 tw:text-[11px] tw:font-medium"
      >
        {{ t("generic.status.optionalBracket") }}
      </div>
    </div>
    <div
      v-if="imgSrc"
      class="tw:mb-1 tw:flex tw:min-h-0 tw:w-full tw:flex-1 tw:items-center tw:justify-center tw:overflow-hidden tw:max-h-[55%]"
    >
      <img
        :src="imgSrc"
        alt=""
        class="tw:max-h-full tw:max-w-full tw:object-contain tw:transition-opacity tw:duration-200"
        :class="[
          isHovered ? 'tw:opacity-100 tw:scale-102' : 'tw:opacity-70',
          themeAwareIcon ? 'tw:dark:brightness-0 tw:dark:invert' : '',
        ]"
      />
    </div>

    <v-icon
      v-else-if="icon"
      :icon="icon"
      size="28"
      class="tw:mb-1 tw:shrink-0 tw:transition-all tw:duration-200 tw:text-secondary"
      :class="isHovered ? 'tw:scale-110' : ''"
    />

    <div
      v-else
      class="tw:mb-1 tw:flex tw:shrink-0 tw:items-center tw:justify-center"
    >
      <v-avatar
        size="50"
        class="tw:bg-black/10 tw:transition-transform tw:duration-200"
        :class="isHovered ? 'tw:scale-105' : ''"
      >
        <v-icon
          icon="mdi-plus-thick"
          size="20"
        />
      </v-avatar>
    </div>

    <div class="upload-field-hint tw:shrink-0">
      <span
        v-if="isTouch"
        class="tw:font-medium tw:text-on-surface/80"
        >{{ t("generic.file.tapToUpload") }}</span
      >

      <template v-else>
        <span class="tw:font-medium tw:text-on-surface/80">{{
          t("generic.file.dragAndDrop")
        }}</span>
        <span class="tw:text-xs tw:text-on-surface/60">{{ t("generic.file.clickToBrowse") }}</span>
      </template>
    </div>
  </div>
</template>

<style scoped>
.avatar-placeholder-shadow {
  box-shadow: 0px 4px 32px -8px rgba(var(--v-theme-secondary), 0.4);
}
</style>
